import { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import permissionService from '../services/permissionService';
import './TopBar.css';

const TopBar = ({ onMenuToggle }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSubsidiaryMenuOpen, setIsSubsidiaryMenuOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const subsidiaryMenuRef = useRef(null);

  // Subsidiaries configuration
  const subsidiaries = {
    LH: "Local Heroes",
    FRB: "Froot Bar",
    CN: "ChickeNow",
    TNL: "The New Luxury",
    AAD: "Apple A Day",
  };

  const [selectedSubsidiaryKey, setSelectedSubsidiaryKey] = useState("LH");
  const [selectedSubsidiaryValue, setSelectedSubsidiaryValue] = useState(subsidiaries["LH"]);
  const [canViewSubsidiaries, setCanViewSubsidiaries] = useState(false);

  // Load selected subsidiary from localStorage on mount
  useEffect(() => {
    const savedKey = localStorage.getItem("selectedSubsidiaryKey");
    const savedValue = localStorage.getItem("selectedSubsidiaryValue");
    if (savedKey && savedValue) {
      setSelectedSubsidiaryKey(savedKey);
      setSelectedSubsidiaryValue(savedValue);
    }
  }, []);

  // Check permission to view subsidiaries
  useEffect(() => {
    let isMounted = true;
    if (!user) return;

    const checkSubsidiaryPermission = async () => {
      try {
        // Check if user has permission to view subsidiaries
        // Using 'subsidiaries.read' as the permission name
        const result = await permissionService.checkPermission('subsidiaries.read');
        if (isMounted && result.success) {
          setCanViewSubsidiaries(result.hasPermission);
        }
      } catch (err) {
        console.error('Failed to check subsidiary permission:', err);
        if (isMounted) {
          setCanViewSubsidiaries(false);
        }
      }
    };

    checkSubsidiaryPermission();
    return () => { isMounted = false; };
  }, [user]);

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const toggleSubsidiaryMenu = () => {
    setIsSubsidiaryMenuOpen(!isSubsidiaryMenuOpen);
  };

  const handleSubsidiarySelect = (key) => {
    const selectedValue = subsidiaries[key];
    localStorage.setItem("selectedSubsidiaryKey", key);
    localStorage.setItem("selectedSubsidiaryValue", selectedValue);
    setSelectedSubsidiaryKey(key);
    setSelectedSubsidiaryValue(selectedValue);
    setIsSubsidiaryMenuOpen(false);
    // Reload the page to apply the subsidiary change
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleProfile = () => {
    navigate('/profile');
    setIsUserMenuOpen(false);
  };

  const handleChangePassword = () => {
    navigate('/change-password');
    setIsUserMenuOpen(false);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (subsidiaryMenuRef.current && !subsidiaryMenuRef.current.contains(event.target)) {
        setIsSubsidiaryMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="top-bar">
      <div className="top-bar-content">
        {/* Hamburger Menu Button - Only visible on mobile */}
        <button className="mobile-menu-button" onClick={onMenuToggle} aria-label="Toggle menu">
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Logo Section */}
        <div className="top-bar-logo">
          <img src="/localheros_logo.webp" alt="Local Heros" className="top-logo" />
        </div>

        {/* Right Section: Subsidiary Dropdown + User Menu */}
        <div className="top-bar-right">
          {/* Subsidiary Dropdown - Only show if user has permission */}
          {canViewSubsidiaries && (
            <div className="subsidiary-dropdown-container" ref={subsidiaryMenuRef}>
              <div className="subsidiary-trigger" onClick={toggleSubsidiaryMenu}>
                <span className="subsidiary-icon">🏢</span>
                <span className="subsidiary-name">{selectedSubsidiaryValue}</span>
                <span className={`dropdown-arrow ${isSubsidiaryMenuOpen ? 'open' : ''}`}>▼</span>
              </div>

              {/* Subsidiary Dropdown Menu */}
              {isSubsidiaryMenuOpen && (
                <div className="subsidiary-dropdown">
                  <ul className="dropdown-menu">
                    {Object.entries(subsidiaries).map(([key, value]) => (
                      <li
                        key={key}
                        className={`dropdown-item ${key === selectedSubsidiaryKey ? 'active' : ''}`}
                        onClick={() => handleSubsidiarySelect(key)}
                      >
                        <span>{value}</span>
                        {key === selectedSubsidiaryKey && <span className="check-icon">✓</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* User Section */}
          <div className="top-bar-user" ref={menuRef}>
            <div className="user-trigger" onClick={toggleUserMenu}>
              <div className="user-avatar">
                <svg className="avatar-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <span className="user-display-name">{user?.name || user?.username}</span>
              <span className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`}>▼</span>
            </div>

            {/* User Dropdown Menu */}
            {isUserMenuOpen && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-user-name">{user?.name || user?.username}</div>
                  <div className="dropdown-user-role">{user?.role}</div>
                </div>

                <div className="dropdown-divider"></div>

                <ul className="dropdown-menu">
                  <li className="dropdown-item" onClick={handleProfile}>
                    <span className="dropdown-icon">👤</span>
                    <span>Profile</span>
                  </li>
                  <li className="dropdown-item" onClick={handleChangePassword}>
                    <span className="dropdown-icon">🔑</span>
                    <span>Change Password</span>
                  </li>
                  <li className="dropdown-divider"></li>
                  <li className="dropdown-item logout" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    <span>Logout</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
